import { memo } from "react";
import { Check } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { GraduationPaymentMethod } from "../types/graduation.types";

const OPTIONS: Array<{ value: GraduationPaymentMethod; label: string }> = [
  { value: "PIX", label: "PIX" },
  { value: "CREDIT_CARD", label: "Crédito" },
  { value: "DEBIT_CARD", label: "Débito" },
  { value: "CASH", label: "Dinheiro" },
];

interface PaymentMethodChipFilterProps {
  value: GraduationPaymentMethod[];
  onChange: (methods: GraduationPaymentMethod[]) => void;
  disabled?: boolean;
}

/**
 * Linha de chips fixos (PIX, Crédito, Débito, Dinheiro) com toggle
 * multi-select. Usado no bloco de filtros — chips sempre visíveis.
 *
 * @example
 * <PaymentMethodChipFilter
 *   value={selectedMethods}
 *   onChange={setSelectedMethods}
 * />
 */
export const PaymentMethodChipFilter = memo(function PaymentMethodChipFilter({
  value,
  onChange,
  disabled,
}: PaymentMethodChipFilterProps) {
  const toggle = (method: GraduationPaymentMethod) => {
    if (value.includes(method)) {
      onChange(value.filter((current) => current !== method));
    } else {
      onChange([...value, method]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {OPTIONS.map((option) => {
        const selected = value.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggle(option.value)}
            disabled={disabled}
            aria-pressed={selected}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm border transition-colors",
              selected
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:border-primary/40",
              disabled && "opacity-60 cursor-not-allowed",
            )}
          >
            {selected && <Check className="w-3.5 h-3.5" />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
});
